/**
 * @fileoverview
 * Definições de blocos para extensões customizadas.
 * Este módulo define os blocos que aparecem em cada categoria de extensão customizada.
 * 
 * IMPORTANTE: Estes blocos precisam ser registrados no VM para funcionarem completamente.
 * Por enquanto, criamos o XML dos blocos para que apareçam no toolbox.
 * A implementação real dos blocos deve ser feita no VM ou através de uma extensão de dispositivo.
 */

const categorySeparator = '<sep gap="36"/>';

/**
 * Constrói XML de um bloco simples compatível com o formato do toolbox.
 * Os blocos criados aqui precisam ter opcodes únicos que serão registrados no VM.
 */
/**
 * Constrói XML de um bloco simples compatível com o formato do toolbox.
 * Os blocos criados aqui precisam ter opcodes únicos que serão registrados no ScratchBlocks.
 */
/**
 * Constrói XML de um bloco simples compatível com o formato do toolbox.
 * Os blocos criados aqui precisam ter opcodes únicos que serão registrados no ScratchBlocks.
 * 
 * Formato baseado no que o VM gera: <block type="opcode"><value name="ARG"><shadow>...</shadow></value></block>
 */
const buildBlockXml = (opcode, text, blockType = 'command', args = {}) => {
    let argsXml = '';
    for (const [argName, argConfig] of Object.entries(args)) {
        if (argConfig.menu === 'analogPins') {
            // Para pinos analógicos, usa um shadow simples com field
            // O menu será resolvido pelo bloco registrado
            argsXml += `<value name="${argName}">
                <shadow type="field_dropdown">
                    <field name="VAR">${argConfig.defaultValue || 'A0'}</field>
                </shadow>
            </value>`;
        } else if (argConfig.menu === 'digitalPins' || argConfig.menu === 'pins') {
            // Para pinos digitais
            argsXml += `<value name="${argName}">
                <shadow type="field_dropdown">
                    <field name="VAR">${argConfig.defaultValue || '2'}</field>
                </shadow>
            </value>`;
        } else if (argConfig.menu === 'pwmPins') {
            // Para pinos PWM
            argsXml += `<value name="${argName}">
                <shadow type="field_dropdown">
                    <field name="VAR">${argConfig.defaultValue || '3'}</field>
                </shadow>
            </value>`;
        } else if (argConfig.type === 'number') {
            argsXml += `<value name="${argName}">
                <shadow type="math_number">
                    <field name="NUM">${argConfig.defaultValue || '0'}</field>
                </shadow>
            </value>`;
        } else {
            argsXml += `<value name="${argName}">
                <shadow type="text">
                    <field name="TEXT">${argConfig.defaultValue || ''}</field>
                </shadow>
            </value>`;
        }
    }
    
    // Adiciona mutation se necessário (para blocos dinâmicos)
    const mutation = '';
    
    return `<block type="${opcode}">${mutation}
${argsXml}
</block>`;
};

/**
 * Blocos para Photo Resistor Sensor (LDR)
 */
export const buildPhotoResistorBlocks = () => {
    return [
        buildBlockXml(
            'photoResistorSensor_readValue',
            'ler valor do fotoresistor pino [PIN]',
            'reporter',
            {
                PIN: {
                    menu: 'analogPins',
                    defaultValue: 'A0',
                    shadowType: 'field_dropdown'
                }
            }
        )
    ].join('\n');
};

/**
 * Blocos para Ultrasonic Sensor (HC-SR04)
 */
export const buildUltrasonicBlocks = () => {
    return [
        buildBlockXml(
            'ultrasonicSensor_readDistance',
            'ler distância sensor ultrassônico trigger [TRIGGER] echo [ECHO]',
            'reporter',
            {
                TRIGGER: {
                    menu: 'digitalPins',
                    defaultValue: '9',
                    shadowType: 'field_dropdown'
                },
                ECHO: {
                    menu: 'digitalPins',
                    defaultValue: '10',
                    shadowType: 'field_dropdown'
                }
            }
        )
    ].join('\n');
};

/**
 * Blocos para Linetracing IR Sensor
 */
export const buildLinetracingIRBlocks = () => {
    return [
        buildBlockXml(
            'linetracingIRSensor_readValue',
            'ler valor sensor IR rastreamento de linha pino [PIN]',
            'reporter',
            {
                PIN: {
                    menu: 'analogPins',
                    defaultValue: 'A0',
                    shadowType: 'field_dropdown'
                }
            }
        ),
        buildBlockXml(
            'linetracingIRSensor_isBlack',
            'sensor IR detecta linha preta pino [PIN]',
            'boolean',
            {
                PIN: {
                    menu: 'analogPins',
                    defaultValue: 'A0',
                    shadowType: 'field_dropdown'
                }
            }
        )
    ].join('\n');
};

/**
 * Blocos para Liquid Crystal 16x2 I2C
 */
export const buildLiquidCrystalI2cBlocks = () => {
    return [
        '<block type="liquidCrystalI2c_init">',
        '<field name="ADDR">0x27</field>',
        '<field name="COLS">16</field>',
        '<field name="ROWS">2</field>',
        '</block>',
        '<block type="liquidCrystalI2c_printAt">',
        '<field name="ADDR">0x27</field>',
        '<field name="ROW">0</field>',
        '<field name="COL">0</field>',
        '<value name="TEXT"><shadow type="text"><field name="TEXT">Olá</field></shadow></value>',
        '</block>',
        '<block type="liquidCrystalI2c_clear">',
        '<field name="ADDR">0x27</field>',
        '</block>'
    ].join('\n');
};

/**
 * Blocos para DHT11 Sensor
 */
export const buildDHT11Blocks = () => {
    return [
        buildBlockXml(
            'dht11Sensor_readTemperature',
            'ler temperatura DHT11 pino [PIN]',
            'reporter',
            {
                PIN: {
                    menu: 'digitalPins',
                    defaultValue: '2',
                    shadowType: 'field_dropdown'
                }
            }
        ),
        buildBlockXml(
            'dht11Sensor_readHumidity',
            'ler umidade DHT11 pino [PIN]',
            'reporter',
            {
                PIN: {
                    menu: 'digitalPins',
                    defaultValue: '2',
                    shadowType: 'field_dropdown'
                }
            }
        )
    ].join('\n');
};

/**
 * Blocos para TCRT5000 Digital (apenas field_dropdown, sem input_value)
 */
export const buildTcrt5000DigitalBlocks = () => {
    return '<block type="tcrt5000Digital_readValue"><field name="PIN">2</field></block>';
};

/**
 * Blocos para Ponte H TB6612FNG
 */
export const buildTb6612fngBlocks = () => {
    return [
        '<block type="tb6612fng_init">',
        '<field name="PWMA">5</field><field name="AIN1">6</field><field name="AIN2">7</field>',
        '<field name="PWMB">9</field><field name="BIN1">10</field><field name="BIN2">11</field>',
        '</block>',
        '<block type="tb6612fng_motorA">',
        '<field name="DIR">forward</field><field name="SPEED">200</field>',
        '</block>',
        '<block type="tb6612fng_motorB">',
        '<field name="DIR">forward</field><field name="SPEED">200</field>',
        '</block>'
    ].join('\n');
};

/**
 * Blocos para Ponte H L298N
 */
export const buildL298nBlocks = () => {
    return [
        '<block type="l298n_init">',
        '<field name="ENA">5</field><field name="IN1">6</field><field name="IN2">7</field>',
        '<field name="ENB">9</field><field name="IN3">10</field><field name="IN4">11</field>',
        '</block>',
        '<block type="l298n_motorA">',
        '<field name="DIR">forward</field><field name="SPEED">200</field>',
        '</block>',
        '<block type="l298n_motorB">',
        '<field name="DIR">forward</field><field name="SPEED">200</field>',
        '</block>'
    ].join('\n');
};

/**
 * Blocos para MPU-6050 (acelerômetro + giroscópio I2C)
 */
export const buildMpu6050Blocks = () => {
    const blocks = [
        '<block type="mpu6050_init"><field name="ADDR">0x68</field></block>',
        '<block type="mpu6050_readAccelX"></block>',
        '<block type="mpu6050_readAccelY"></block>',
        '<block type="mpu6050_readAccelZ"></block>',
        '<block type="mpu6050_readGyroX"></block>',
        '<block type="mpu6050_readGyroY"></block>',
        '<block type="mpu6050_readGyroZ"></block>',
        '<block type="mpu6050_readTemperature"></block>'
    ];
    return blocks.join('\n');
};

/**
 * Mapeia extensionId para função de construção de blocos
 */
export const getBlocksBuilder = (extensionId) => {
    const builders = {
        'photoResistorSensor': buildPhotoResistorBlocks,
        'ultrasonicSensor': buildUltrasonicBlocks,
        'linetracingIRSensor': buildLinetracingIRBlocks,
        'dht11Sensor': buildDHT11Blocks,
        'liquidCrystalI2c': buildLiquidCrystalI2cBlocks,
        'tb6612fng': buildTb6612fngBlocks,
        'l298n': buildL298nBlocks,
        'tcrt5000Digital': buildTcrt5000DigitalBlocks,
        'mpu6050': buildMpu6050Blocks
    };
    return builders[extensionId] || null;
};

/**
 * Constrói XML completo da categoria com blocos
 */
export const buildCategoryWithBlocks = (extensionId, categoryName, categoryColor, categorySecondaryColor) => {
    const blocksBuilder = getBlocksBuilder(extensionId);
    if (!blocksBuilder) return null;
    
    const blocksXml = blocksBuilder();
    return `<category name="${categoryName}" id="${extensionId}" colour="${categoryColor}" secondaryColour="${categorySecondaryColor}">
${blocksXml}
${categorySeparator}
</category>`;
};
